import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { showAlert } from '../appToast/appToastSlice';

interface IResendLoginOtpInput {
  sessionToken: string;
}

interface IResendLoginOtpResponse {
  status?: boolean;
  message?: string;
  data?: { sessionToken?: string };
  errors?: IApiResponseError[];
}

export interface IResendLoginOtpRejectPayload extends IResendLoginOtpResponse {
  unauthorized?: boolean;
}

const initialState: IApiState<IResendLoginOtpResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const resendLoginOtpAsync = createAsyncThunk<
  IResendLoginOtpResponse,
  IResendLoginOtpInput,
  { rejectValue: IResendLoginOtpRejectPayload }
>(
  'auth/resendLoginOtp',
  async ({ sessionToken }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.post<IResendLoginOtpResponse>(
        '/auth/login/resend-otp',
        { sessionToken },
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'OTP has been resent',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IResendLoginOtpResponse>;
      const isUnauthorized = error?.response?.status === 401;

      dispatch(
        showAlert({
          status: 'error',
          description: isUnauthorized
            ? 'Your session has expired. Please sign in again.'
            : (error?.response?.data?.message ??
              'Could not resend OTP. Please try again.'),
        }),
      );

      return rejectWithValue({
        ...(error.response?.data ?? {}),
        unauthorized: isUnauthorized,
      });
    }
  },
);

export const resendLoginOtpSlice = createSlice({
  name: 'auth/resendLoginOtp',
  initialState,
  reducers: {
    resetResendLoginOtp: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resendLoginOtpAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(resendLoginOtpAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(resendLoginOtpAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IResendLoginOtpResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetResendLoginOtp } = resendLoginOtpSlice.actions;

export default resendLoginOtpSlice.reducer;
